import React, { useCallback, useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { flush, queueCounts } from "@/pwa/offlineQueue";
import { onUpdateAvailable, applyUpdate } from "@/pwa/registerSW";
import { Button } from "@/components/ui/button";
import { Wifi, WifiOff, RefreshCw, CloudUpload, AlertTriangle } from "lucide-react";

// Honest connectivity indicator + offline queue status + safe update prompt. Mounted in AppShell.
export const PwaStatus: React.FC = () => {
  const { user, activeMembership } = useAuth();
  const { t } = useI18n();
  const userId = user?.id || "";
  const tenantId = (activeMembership as any)?.tenant_id || "";
  const [online, setOnline] = useState<boolean>(navigator.onLine);
  const [reconnecting, setReconnecting] = useState(false);
  const [counts, setCounts] = useState({ queued: 0, failed: 0, conflict: 0, total: 0 });
  const [updateReady, setUpdateReady] = useState(false);

  const refresh = useCallback(() => {
    if (userId) setCounts(queueCounts(userId));
  }, [userId]);

  const doFlush = useCallback(async () => {
    if (!userId || !tenantId || !navigator.onLine) return;
    setReconnecting(true);
    try { await flush(API, tenantId, userId); } finally { setReconnecting(false); refresh(); }
  }, [userId, tenantId, refresh]);

  useEffect(() => {
    refresh();
    const goOnline = () => { setOnline(true); doFlush(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const onQueueChange = () => refresh();
    window.addEventListener("sc-queue-changed", onQueueChange);
    onUpdateAvailable(() => setUpdateReady(true));
    if (navigator.onLine) doFlush();
    const t = setInterval(refresh, 4000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("sc-queue-changed", onQueueChange);
      clearInterval(t);
    };
  }, [refresh, doFlush]);

  if (!userId) return null;
  return (
    (<div className="flex items-center gap-2" data-testid="pwa-status">
      {updateReady && (
        <div className="flex items-center gap-2 rounded-full bg-brand px-3 py-1 text-xs text-white" data-testid="pwa-update-banner">
          <span>{t("New version available")}</span>
          <Button size="sm" variant="secondary" className="h-6 px-2 py-0 text-xs" onClick={() => applyUpdate()} data-testid="pwa-update-apply">{t("Update")}</Button>
        </div>
      )}
      {counts.total > 0 && (
        <div className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs" data-testid="pwa-queue">
          <CloudUpload size={13} />
          <span data-testid="pwa-queue-count">{counts.queued}{" "}{t("queued")}</span>
          {counts.conflict > 0 && <span className="text-amber-600" data-testid="pwa-queue-conflict">· {counts.conflict}{" "}{t("conflict")}</span>}
          {counts.failed > 0 && <span className="text-red-600" data-testid="pwa-queue-failed">· {counts.failed}{" "}{t("failed")}</span>}
        </div>
      )}
      <div className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${online ? "bg-emerald-600/10 text-emerald-700" : "bg-red-500/10 text-red-600"}`}
        data-testid="pwa-connectivity" data-online={online} title={online ? t("common.online") : t("common.offline")}>
        {reconnecting ? <RefreshCw size={13} className="animate-spin" /> : online ? <Wifi size={13} /> : <WifiOff size={13} />}
        <span data-testid="pwa-connectivity-label">{reconnecting ? t("common.syncing") : online ? t("common.online") : t("common.offline")}</span>
      </div>
    </div>)
  );
};
